import React, { FC, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { Box, ClickAwayListener, FormHelperText, Grid } from '@mui/material'
import { Colors } from '../../theme'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'

const useStyles = makeStyles((theme: Theme) => ({
  phoneWrap: {
    width: 300,
    maxHeight: 63,
    marginBottom: 20,
    '& .special-label': {
      display: 'block',
      position: 'static',
      background: 'transparent',
      marginBottom: '3px',
      padding: 0,
      color: theme.palette.primary.light,
      opacity: 0.6,
      fontSize: 14,
      lineHeight: '20px',
    },
    '& .form-control': {
      height: 40,
      color: theme.palette.primary.light,
      background: 'transparent',
      borderRadius: 4,
      '&:hover': {
        borderColor: theme.palette.primary.light,
        opacity: 1,
        '&+.flag-dropdown': {
          borderColor: theme.palette.primary.light,
          opacity: 1,
        },
      },
    },
    '& .flag-dropdown': {
      height: 40,
      top: 23,
      background: 'transparent',
      border: 0,
      '& .selected-flag:focus': {
        background: 'transparent',
      },
      '&:hover': {
        borderColor: theme.palette.primary.light,
        opacity: 1,
        '& .selected-flag': {
          background: 'transparent !important',
        },
      },
      '&.open': {
        background: 'transparent',
        color: theme.palette.primary.light,
        '& .arrow': {
          borderBottom: `4px solid ${theme.palette.primary.light}`,
          opacity: 0.6,
        },
      },
      '&.open .selected-flag': {
        background: 'transparent',
      },

      '& .arrow': {
        borderTop: `4px solid ${theme.palette.primary.light}`,
        opacity: 0.6,
      },
      '& .country-list': {
        background: '#404247',
        '& .highlight': {
          background: 'grey',
        },
        '& .country:hover': {
          background: 'grey',
        },
      },
    },
  },
  phoneError: {
    position: 'relative',
    top: 2,
    width: 300,
    marginTop: -20,
    color: Colors.error,
  },
}))

interface IInputPhone {
  phoneError?: string | boolean
  defaultValue?: string
}

export const InputPhone: FC<IInputPhone> = ({ phoneError, defaultValue }) => {
  const classes = useStyles()
  const [isFocus, setIsFocus] = useState<boolean>(false)

  const {
    register,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext()

  useEffect(() => {
    register('phone', {
      value: defaultValue,
      required: true,
    })
  }, [defaultValue, register])

  const errorMessage = useMemo(
    () => (errors?.phone ? capitalizeFirstLetter(errors?.phone.message.replace('phone', '')) : ''),
    [errors?.phone],
  )

  return (
    <ClickAwayListener onClickAway={() => setIsFocus(false)}>
      <Box
        onFocus={() => setIsFocus(true)}
        onBlur={() => {
          setIsFocus(false)
          trigger('phone')
        }}
      >
        <PhoneInput
          value={defaultValue}
          country={'us'}
          specialLabel={'Phone Number'}
          containerClass={classes.phoneWrap}
          defaultErrorMessage={errors.phone && errors.phone.message}
          inputStyle={
            /* eslint-disable-next-line no-nested-ternary */
            isFocus
              ? { borderColor: '#51E5A5' }
              : errors.phone || phoneError
              ? { borderColor: Colors.error }
              : undefined
          }
          buttonStyle={isFocus ? { borderColor: '#51E5A5' } : undefined}
          inputProps={{
            name: 'phone',
          }}
          onChange={(value) => {
            setValue('phone', `+${value}`, { shouldDirty: true, shouldValidate: true })
          }}
        />

        <Grid item>
          {(phoneError || errors.phone) && (
            <FormHelperText className={classes.phoneError}>{phoneError || errorMessage}</FormHelperText>
          )}
        </Grid>
      </Box>
    </ClickAwayListener>
  )
}
